import * as React from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { vw } from "../../../lib/utils/responsiveUtils";
import { staticURL } from "../../../lib/utils/imageUtils";
import AnimatedCoverTabs from "../../atoms/tab/AnimatedConverTab";
import type { getResveGolfParkSearchRequest } from "../../../@apis/dto/request/resve.request";
import { useGetResveGolfParkSearch } from "../../../@apis/hooks/useResve";
import { useNavigate } from "react-router-dom";
import { useModalStore } from "../../../lib/store/useModalStore";
import dayjs from "dayjs";

interface Props {
  date: string;
}

const TAB_LABELS = [
  { label: "수도권", value: "1" },
  { label: "충청권", value: "2" },
  { label: "강원권", value: "3" },
  { label: "호남권", value: "4" },
  { label: "영남권", value: "5" },
];

export default function ReservationSearchLayout({ date }: Props) {
  const navigate = useNavigate();
  const { openModal } = useModalStore();
  const [form, setForm] = React.useState<getResveGolfParkSearchRequest>({
    page: 1,
    pageSize: 10,
    latitude: 0,
    longitude: 0,
    area: 1,
    date: date,
  });

  React.useEffect(() => {
    if (date) setForm((prev) => ({ ...prev, date: date }));
  }, [date]);

  // 검색어
  const [typing, setTyping] = React.useState("");

  const listRef = React.useRef<HTMLDivElement>(null);

  // 검색어 없으면 pageSize 안 보냄(= 전체)
  const {
    data: res,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    ...useGetResveGolfParkSearch(form),
  });
  const golfParkPages = res?.pages || [];

  const triggerRef = React.useRef(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: listRef.current, threshold: 0 },
    );

    const currentTrigger = triggerRef.current;
    if (currentTrigger) {
      observer.observe(currentTrigger);
    }

    return () => {
      if (currentTrigger) {
        observer.unobserve(currentTrigger);
      }
    };
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  React.useEffect(() => {
    const t = setTimeout(() => {
      setForm((prev) => ({
        ...prev,
        keyword: typing.trim() === "" ? undefined : typing.trim(),
      }));
    }, 500);

    return () => clearTimeout(t);
  }, [typing]);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const scrollListToTop = React.useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  React.useEffect(() => {
    scrollListToTop();
  }, [form.area, form.date, form.keyword, scrollListToTop]);

  return (
    <>
      <div className="flex flex-col h-full min-h-0">
        <section className="mb-[25px] space-y-3 shrink-0">
          {/* 검색창 */}
          <div className="rounded-[10px] border border-[#e6e6e6] mb-[15px] px-[15px] flex items-center gap-[10px] bg-white py-[18px] px-[20px]">
            <img
              className="size-[24px]"
              src="/icons/ic-search.webp"
              alt="검색"
            />
            <input
              ref={inputRef}
              value={typing}
              onChange={(e) => setTyping(e.target.value)}
              placeholder="골프장명을 입력해주세요."
              className="flex-1 outline-none placeholder:text-[#ccc] text-[0.7rem] placeholder:text-[0.7rem]"
            />
          </div>

          <AnimatedCoverTabs
            tabs={TAB_LABELS}
            value={form.area?.toString() || ""}
            onChange={(value) => {
              setForm((prev) => ({ ...prev, area: Number(value), page: 1 }));
              scrollListToTop();
            }}
          />
        </section>

        {/* 리스트 영역 */}
        <section
          className="pb-[50px] flex-1 min-h-0 flex flex-col space-y-3 bg-[#f5f5f5] overflow-y-auto"
          ref={listRef}
        >
          {golfParkPages.every(
            (p) =>
              (p.data.golfParkList ?? []).filter((item) => item.resveAvailable)
                .length === 0,
          ) ? (
            <div className="text-center text-[#888] pt-[20px]">
              예약 가능한 골프장이 없습니다.
            </div>
          ) : (
            golfParkPages.map((pageData, pageIndex) => {
              const availableList = (pageData.data.golfParkList || []).filter(
                (item) => item.resveAvailable,
              );

              return (
                <React.Fragment key={pageIndex}>
                  {availableList.map((item, idx, arr) => {
                    const isLast =
                      pageIndex === golfParkPages.length - 1 &&
                      idx === arr.length - 1;

                    return (
                      <div
                        key={item.code}
                        onClick={() => {
                          const isToday = dayjs(form.date).isSame(
                            dayjs(),
                            "day",
                          );

                          if (isToday) {
                            openModal("CONFIRM_DIALOG", {
                              title: "당일 예약 안내",
                              content:
                                "당일 예약은 예약 취소가 불가합니다.\n그래도 예약을 진행하시겠습니까?",
                              onClick: () => {
                                navigate("/reservation/add", {
                                  state: {
                                    golfParkCode: item.code,
                                    date: form.date,
                                  },
                                });
                              },
                            });
                            return;
                          }

                          navigate("/reservation/add", {
                            state: {
                              golfParkCode: item.code,
                              date: form.date,
                            },
                          });
                        }}
                        className="bg-white rounded-[15px] p-[20px] mb-[15px] flex items-center gap-[19px]"
                        style={{ height: vw(123) }}
                        ref={isLast ? triggerRef : undefined}
                      >
                        <img
                          src={
                            item.imageUrl
                              ? staticURL(item.imageUrl)
                              : "/images/img-login-logo.webp"
                          }
                          alt={item.name}
                          className="rounded-[10px] object-cover bg-[#eee] shrink-0"
                          style={{ width: vw(83), height: vw(83) }}
                        />
                        <div>
                          <p
                            className="font-bold text-[#222] line-clamp-1"
                            style={{ fontSize: vw(20) }}
                          >
                            {item.name}
                          </p>
                          <div className="relative mt-[14px] pl-[20px]">
                            <img
                              src="/icons/ic-place-gray.webp"
                              alt="place"
                              className="absolute top-[1px] left-0"
                              style={{ width: vw(14), height: vw(14) }}
                            />
                            <p
                              className="text-[#888] font-[500] leading-[1.5] line-clamp-2"
                              style={{ fontSize: vw(12) }}
                            >
                              {item.address}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              );
            })
          )}
        </section>
      </div>
    </>
  );
}
